import { useEffect, useCallback, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { Table, TableBody, TableCell, TableHead, TableFooter, TableRow, Paper, Typography, Grid, Box, IconButton, Tooltip } from '@mui/material';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

import { formatMoney, formatBrDate } from '../../shared/formatters';
import { loadStart, getMonthKey, getPreviousMonthValue, setViewMode } from '../../redux/monthlyBalanceSlice';
import { loadProperty } from '../../redux/propertySlice';
import { downloadCSV } from '../../shared/utils';
import Filter from './filter';
import GroupComponent, { getMonthComparison, formatSectionMoney } from './groupComponent';
import TransactionList, { sortTransactionsByDate } from './transactionList';

const StyledPaper = styled(Paper)(({ theme }) => ({
  width: '100%',
  overflowX: 'auto',
  marginTop: theme.spacing(3),
  borderRadius: '16px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  scrollBehavior: 'smooth',
  '&::-webkit-scrollbar': {
    height: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'rgba(0, 0, 0, 0.05)',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(0, 98, 105, 0.3)',
    borderRadius: '4px',
    '&:hover': {
      background: 'rgba(0, 98, 105, 0.5)',
    },
  },
}));

const StyledTable = styled(Table)(({ theme }) => ({
  minWidth: 600,
  width: '100%',
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  transition: 'background-color 0.2s ease',
  '&:hover': {
    backgroundColor: 'rgba(0, 98, 105, 0.04)',
  },
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  backgroundColor: 'rgba(0, 98, 105, 0.05)',
  '& .MuiTableCell-head': {
    fontWeight: 700,
    color: theme.palette.text.primary,
    fontSize: '0.875rem',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontSize: '0.938rem',
  fontWeight: 500,
  borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
}));

const MoneyCell = styled(TableCell)(({ theme }) => ({
  fontSize: '0.938rem',
  fontWeight: 500,
  padding: '16px',
  whiteSpace: 'nowrap',
  width: '220px',
  minWidth: '220px',
  borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
}));

const SummaryCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: '16px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  height: '100%',
}));

const SummaryLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.813rem',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  color: theme.palette.text.secondary,
}));

const SummaryValue = styled(Typography)(({ theme }) => ({
  fontSize: '1.75rem',
  fontWeight: 700,
  whiteSpace: 'nowrap',
}));

const EmptyState = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  textAlign: 'center',
  color: theme.palette.text.secondary,
}));

const ComparisonText = styled('span', {
  shouldForwardProp: (prop) => prop !== 'isGood',
})(({ theme, isGood }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: '4px',
  width: '130px',
  fontSize: '0.75rem',
  fontWeight: 500,
  color: theme.palette.text.secondary,
  whiteSpace: 'nowrap',
  opacity: 0.9,
}));

const PercentageText = styled('span', {
  shouldForwardProp: (prop) => prop !== 'isGood',
})(({ theme, isGood }) => ({
  display: 'inline-block',
  width: '62px',
  textAlign: 'right',
  fontSize: '0.75rem',
  fontWeight: 600,
  color: isGood ? theme.palette.success.dark : theme.palette.error.dark,
  whiteSpace: 'nowrap',
}));

const TotalValueText = styled('span')({
  display: 'inline-block',
  width: '120px',
  textAlign: 'right',
  whiteSpace: 'nowrap',
});

const TotalWithComparison = ({ currentValue, previousValue, isDebit }) => {
  const comparison = previousValue !== undefined && previousValue !== null
    ? getMonthComparison(currentValue, previousValue, isDebit)
    : null;

  if (!comparison || Math.abs(comparison.difference) < 0.005) {
    return (
      <Box component="span" sx={{ display: 'grid', gridTemplateColumns: '130px 120px 62px', justifyContent: 'end', alignItems: 'center', columnGap: '3px', textAlign: 'right', width: '100%' }}>
        <ComparisonText isGood={true} sx={{ visibility: 'hidden' }}>
          0
        </ComparisonText>
        <TotalValueText>{formatSectionMoney(currentValue)}</TotalValueText>
        <PercentageText isGood={true} sx={{ visibility: 'hidden' }}> (0,0%) </PercentageText>
      </Box>
    );
  }

  const percentageLabel = comparison.percentage !== null
    ? `(${comparison.isIncrease ? '+' : '-'}${Math.abs(comparison.percentage).toFixed(1).replace('.', ',')}%)`
    : '';

  return (
    <Box component="span" sx={{ display: 'grid', gridTemplateColumns: '130px 120px 62px', justifyContent: 'end', alignItems: 'center', columnGap: '3px', textAlign: 'right', width: '100%' }}>
      <ComparisonText isGood={comparison.isGood}>
        {formatSectionMoney(previousValue)}
      </ComparisonText>
      <TotalValueText>{formatSectionMoney(currentValue)}</TotalValueText>
      <PercentageText isGood={comparison.isGood}>{percentageLabel}</PercentageText>
    </Box>
  );
};

function groupTransactionsByCategory(transactions) {
  const grouped = {};
  (transactions || []).forEach(transaction => {
    const categoryId = transaction.category ? transaction.category.id : null;
    if (categoryId === null || categoryId === undefined) return;
    if (!grouped[categoryId]) grouped[categoryId] = [];
    grouped[categoryId].push(transaction);
  });

  Object.values(grouped).forEach(items => {
    items.sort((first, second) => {
      if (first.date === second.date) return (second.value || 0) - (first.value || 0);
      return first.date < second.date ? -1 : 1;
    });
  });

  return grouped;
}

function toArray(value) {
  return Array.isArray(value) ? value : Object.values(value || {});
}

function Report() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [openGroups, setOpenGroups] = useState({});
  const [openCategories, setOpenCategories] = useState({});

  const result = useSelector(state => state.monthlyBalance.result);
  const transactions = useSelector(state => state.monthlyBalance.transactions);
  const shouldBeUpdated = useSelector(state => state.monthlyBalance.shouldBeUpdated);
  const lastUpdate = useSelector(state => state.monthlyBalance.lastUpdate);
  const month = useSelector(state => state.monthlyBalance.filterFields.month);
  const viewMode = useSelector(state => state.monthlyBalance.viewMode);
  const propertyName = useSelector(state => state.property.name);
  const propertyShouldBeUpdated = useSelector(state => state.property.shouldBeUpdated);

  const loadStartAction = useCallback(() => dispatch(loadStart()), [dispatch]);

  useEffect(() => {
    if (shouldBeUpdated || new Date(lastUpdate).getTime() + 1000 * 10 < new Date().getTime()) {
      loadStartAction();
    }
  }, [shouldBeUpdated, lastUpdate, loadStartAction]);

  useEffect(() => {
    if (propertyShouldBeUpdated) {
      dispatch(loadProperty());
    }
  }, [propertyShouldBeUpdated, dispatch]);

  const transactionsByCategory = useMemo(
    () => groupTransactionsByCategory(transactions),
    [transactions]
  );

  const creditTransactions = useMemo(
    () => sortTransactionsByDate((transactions || []).filter(transaction => transaction.category && transaction.category.type === 'credit')),
    [transactions]
  );

  const debitTransactions = useMemo(
    () => sortTransactionsByDate((transactions || []).filter(transaction => transaction.category && transaction.category.type === 'debit')),
    [transactions]
  );

  const monthKey = getMonthKey(month);
  const previousMonthKey = getMonthKey(getPreviousMonthValue(month));

  const creditGroups = toArray(result.groups && result.groups.credit);
  const debitGroups = toArray(result.groups && result.groups.debit);

  // Both transaction tables should share the same columns so they stay aligned
  const transactionAccountKeys = new Set(
    [...creditTransactions, ...debitTransactions].map(
      transaction => transaction.financeAccount?.id ?? transaction.financeAccount?.name ?? ''
    )
  );
  const showTransactionAccountColumn = transactionAccountKeys.size > 1;

  const toggleGroup = (groupId) => {
    setOpenGroups(previous => ({ ...previous, [groupId]: !previous[groupId] }));
  };

  const toggleCategory = (categoryId) => {
    setOpenCategories(previous => ({ ...previous, [categoryId]: !previous[categoryId] }));
  };

  const handleExpandSection = (groups) => {
    const newGroups = {};
    const newCategories = {};

    groups.forEach(groupData => {
      newGroups[groupData.group.id] = true;
      toArray(groupData.categories).forEach(categoryData => {
        newCategories[categoryData.category.id] = true;
      });
    });

    setOpenGroups(previous => ({ ...previous, ...newGroups }));
    setOpenCategories(previous => ({ ...previous, ...newCategories }));
  };

  const handleCollapseSection = (groups) => {
    const groupIds = new Set(groups.map(groupData => groupData.group.id));
    const categoryIds = new Set(
      groups.flatMap(groupData => toArray(groupData.categories).map(categoryData => categoryData.category.id))
    );

    setOpenGroups(previous => {
      const updated = { ...previous };
      groupIds.forEach(id => { updated[id] = false; });
      return updated;
    });
    setOpenCategories(previous => {
      const updated = { ...previous };
      categoryIds.forEach(id => { updated[id] = false; });
      return updated;
    });
  };

  const isSectionFullyExpanded = (groups) => groups.length > 0 && groups.every(groupData => !!openGroups[groupData.group.id]);

  const buildExportData = (includeEmptyCategories = true) => {
    if (!result.balance) return;

    const exportData = [];

    exportData.push({ secao: 'RESUMO', item: '', data: '', valor: '' });
    exportData.push({ secao: '', item: 'Crédito', data: '', valor: formatMoney(result.creditBalance[monthKey]) });
    exportData.push({ secao: '', item: 'Débito', data: '', valor: formatMoney(result.debitBalance[monthKey]) });
    exportData.push({ secao: '', item: 'Saldo', data: '', valor: formatMoney(result.balance[monthKey]) });
    exportData.push({ secao: '', item: '', data: '', valor: '' });

    const exportSection = (title, groups, sectionType) => {
      exportData.push({ secao: title, item: '', data: '', valor: '' });

      toArray(groups).forEach(groupData => {
        exportData.push({
          secao: '',
          item: groupData.group.name,
          data: '',
          valor: formatMoney(Math.abs(groupData.balance[monthKey] || 0))
        });

        toArray(groupData.categories).forEach(categoryData => {
          const categoryTransactions = transactionsByCategory[categoryData.category.id] || [];
          const categoryValue = Math.abs(categoryData.balance[monthKey] || 0);
          if (!includeEmptyCategories && categoryValue === 0 && categoryTransactions.length === 0) return;

          exportData.push({
            secao: '',
            item: `  ${categoryData.category.name}`,
            data: '',
            valor: formatMoney(categoryValue)
          });

          categoryTransactions.forEach(transaction => {
            exportData.push({
              secao: '',
              item: `    ${transaction.description || 'Sem descrição'}`,
              data: transaction.date,
              valor: `${sectionType === 'debit' ? '-' : '+'}${formatMoney(transaction.value)}`
            });
          });
        });
      });
    };

    exportSection('CRÉDITOS', result.groups.credit, 'credit');
    exportData.push({ secao: '', item: '', data: '', valor: '' });
    exportSection('DÉBITOS', result.groups.debit, 'debit');

    return exportData;
  };

  const handleExport = () => {
    const exportData = buildExportData();
    if (!exportData) return;

    const columnsMap = [
      { field: 'secao', title: 'Seção' },
      { field: 'item', title: 'Item' },
      { field: 'data', title: 'Data' },
      { field: 'valor', title: 'Valor' }
    ];

    downloadCSV(exportData, columnsMap, `relatorio_saldo_mensal_${month}.csv`);
  };

  const buildTransactionExportData = () => {
    const sections = [
      { title: 'CRÉDITOS', items: creditTransactions, isDebit: false },
      { title: 'DÉBITOS', items: debitTransactions, isDebit: true },
    ];
    return sections.map(section => {
      const rows = [];
      section.items.forEach(transaction => {
        rows.push({
          item: transaction.description || 'Sem descrição',
          date: formatBrDate(transaction.date).replace(/\//g, '-'),
          value: `${section.isDebit ? '-' : '+'}${formatMoney(transaction.value)}`,
        });
      });
      rows.push({
        item: `Total ${section.title.charAt(0)}${section.title.slice(1).toLowerCase()}`,
        date: '',
        value: formatMoney(section.items.reduce((sum, transaction) => sum + (parseFloat(transaction.value) || 0), 0)),
        isTotal: true,
      });

      return { title: section.title, rows };
    });
  };

  const handleExportPdf = () => {
    const isTransactionView = viewMode === 'transactions';
    const transactionExport = isTransactionView ? buildTransactionExportData() : null;
    const exportData = isTransactionView ? null : buildExportData(false);
    if (isTransactionView) {
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      pdf.setFontSize(18);
      pdf.setTextColor(0, 0, 0);
      pdf.text(propertyName || 'Relatório mensal de transações', 14, 16);
      pdf.setFontSize(10);
      pdf.setTextColor(90, 90, 90);
      pdf.text(`Saldo do mês - ${monthKey}`, 14, 23);

      let nextTableY = 29;
      transactionExport.forEach(section => {
        pdf.setFontSize(11);
        pdf.setTextColor(0, 0, 0);
        pdf.text(section.title, 14, nextTableY);
        autoTable(pdf, {
          startY: nextTableY + 3,
          head: [['Item', 'Data', 'Valor']],
          body: section.rows.map(row => [row.item, row.date, row.value]),
          theme: 'grid',
          styles: {
            fontSize: 9,
            cellPadding: 2.5,
            lineColor: [0, 0, 0],
            lineWidth: 0.15,
          },
          headStyles: {
            fillColor: [255, 255, 255],
            textColor: [0, 0, 0],
            fontStyle: 'bold',
            lineColor: [0, 0, 0],
            lineWidth: 0.3,
          },
          columnStyles: {
            0: { cellWidth: 115 },
            1: { cellWidth: 28 },
            2: { cellWidth: 35, halign: 'right' },
          },
          didParseCell: data => {
            if (data.section === 'body' && section.rows[data.row.index]?.isTotal) {
              data.cell.styles.fontStyle = 'bold';
              data.cell.styles.fillColor = [255, 255, 255];
            }
          },
        });
        nextTableY = pdf.lastAutoTable.finalY + 12;
      });

      pdf.save(`relatorio_saldo_mensal_${month}.pdf`);
      return;
    }
    if (!exportData) return;

    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });
    pdf.setFontSize(18);
    pdf.setTextColor(0, 0, 0);
    pdf.text(propertyName || 'Relatório mensal', 14, 16);
    pdf.setFontSize(10);
    pdf.setTextColor(90, 90, 90);
    pdf.text(`Saldo do mês - ${monthKey}`, 14, 23);

    const pdfHead = [['Item', 'Data', 'Valor']];
    const pdfBody = exportData.map(row => [row.secao || row.item, row.secao ? '' : row.data, row.secao ? '' : row.valor]);

    autoTable(pdf, {
      startY: 29,
      head: pdfHead,
      body: pdfBody,
      theme: 'grid',
      styles: {
        fontSize: 9,
        cellPadding: 2.5,
        lineColor: [0, 0, 0],
        lineWidth: 0.15,
      },
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        lineColor: [0, 0, 0],
        lineWidth: 0.3,
      },
      columnStyles: {
        0: { cellWidth: 115 },
        1: { cellWidth: 28 },
        2: { cellWidth: 35, halign: 'right' },
      },
      didParseCell: data => {
        const isSectionRow = Boolean(exportData[data.row.index]?.secao);
        if (data.section === 'body' && isSectionRow) {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fillColor = [255, 255, 255];
        }
      },
    });

    pdf.save(`relatorio_saldo_mensal_${month}.pdf`);
  };

  if (!result.balance)
    return <div>No data</div>;

  return (
    <>
      <Typography variant="h4" component="h2" sx={{
        fontWeight: 700,
        color: '#1a1a1a',
        marginBottom: 1,
      }}>
        Saldo do mês
      </Typography>
      <Filter
        onExport={handleExport}
        onExportPdf={handleExportPdf}
        viewMode={viewMode}
        onViewModeChange={(mode) => dispatch(setViewMode(mode))}
      ></Filter>
      <Grid container spacing={2} sx={{ marginTop: 1 }}>
        <Grid size={{ xs: 12, sm: 12, md: 4 }}>
          <SummaryCard elevation={0}>
            <SummaryLabel>Crédito</SummaryLabel>
            <SummaryValue sx={{ color: 'success.dark' }}>
              {formatMoney(result.creditBalance[monthKey])}
            </SummaryValue>
          </SummaryCard>
        </Grid>
        <Grid size={{ xs: 12, sm: 12, md: 4 }}>
          <SummaryCard elevation={0}>
            <SummaryLabel>Débito</SummaryLabel>
            <SummaryValue sx={{ color: 'error.dark' }}>
              {formatMoney(result.debitBalance[monthKey])}
            </SummaryValue>
          </SummaryCard>
        </Grid>
        <Grid size={{ xs: 12, sm: 12, md: 4 }}>
          <SummaryCard elevation={0}>
            <SummaryLabel>Saldo</SummaryLabel>
            <SummaryValue sx={{
              color: result.balance[monthKey] < 0 ? 'error.dark' : 'rgb(0,98,105)'
            }}>
              {formatMoney(result.balance[monthKey])}
            </SummaryValue>
          </SummaryCard>
        </Grid>
      </Grid>
      {viewMode === 'transactions' ? (
        <>
          <TransactionList
            title="Créditos"
            monthKey={monthKey}
            transactions={creditTransactions}
            navigate={navigate}
            showAccountColumn={showTransactionAccountColumn}
          />
          <TransactionList
            title="Débitos"
            monthKey={monthKey}
            transactions={debitTransactions}
            navigate={navigate}
            isDebit
            showAccountColumn={showTransactionAccountColumn}
          />
        </>
      ) : (
        <>
          <StyledPaper>
            <StyledTable>
          <StyledTableHead>
            <TableRow>
              <StyledTableCell>
                Créditos
                {isSectionFullyExpanded(creditGroups) ? (
                  <Tooltip title="Recolher tudo" placement="top">
                    <IconButton size="small" onClick={() => handleCollapseSection(creditGroups)} sx={{ ml: 1 }}>
                      <UnfoldLessIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ) : (
                  <Tooltip title="Expandir tudo" placement="top">
                    <IconButton size="small" onClick={() => handleExpandSection(creditGroups)} sx={{ ml: 1 }}>
                      <UnfoldMoreIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </StyledTableCell>
              <StyledTableCell colSpan={3} align="right">
                <Box sx={{ display: 'grid', gridTemplateColumns: '130px 120px 62px', justifyContent: 'end', columnGap: '3px', textAlign: 'right' }}>
                  <span>Mês anterior</span>
                  <span>{monthKey}</span>
                  <span>%</span>
                </Box>
              </StyledTableCell>
            </TableRow>
          </StyledTableHead>
          <TableBody>
            {creditGroups.length === 0 && (
              <TableRow>
                <StyledTableCell colSpan={4}>
                  <EmptyState>Nenhum crédito no período</EmptyState>
                </StyledTableCell>
              </TableRow>
            )}
            {creditGroups.map((groupData, index) => (
              <GroupComponent
                key={groupData.group.id || index}
                groupData={groupData}
                monthKey={monthKey}
                previousMonthKey={previousMonthKey}
                transactionsByCategory={transactionsByCategory}
                navigate={navigate}
                isOpen={!!openGroups[groupData.group.id]}
                openCategories={openCategories}
                onToggleGroup={toggleGroup}
                onToggleCategory={toggleCategory}
              />
            ))}
          </TableBody>
          <TableFooter>
            <StyledTableRow hover>
              <StyledTableCell sx={{ fontWeight: 700, fontSize: '1rem' }}>
                Total Créditos
              </StyledTableCell>
              <MoneyCell align="right" colSpan={3} sx={{ fontWeight: 700, fontSize: '1rem' }}>
                <TotalWithComparison
                  currentValue={result.creditBalance[monthKey]}
                  previousValue={result.creditBalance[previousMonthKey]}
                  isDebit={false}
                />
              </MoneyCell>
            </StyledTableRow>
          </TableFooter>
        </StyledTable>
      </StyledPaper>
      <StyledPaper>
        <StyledTable>
          <StyledTableHead>
            <TableRow>
              <StyledTableCell>
                Débitos
                {isSectionFullyExpanded(debitGroups) ? (
                  <Tooltip title="Recolher tudo" placement="top">
                    <IconButton size="small" onClick={() => handleCollapseSection(debitGroups)} sx={{ ml: 1 }}>
                      <UnfoldLessIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ) : (
                  <Tooltip title="Expandir tudo" placement="top">
                    <IconButton size="small" onClick={() => handleExpandSection(debitGroups)} sx={{ ml: 1 }}>
                      <UnfoldMoreIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </StyledTableCell>
              <StyledTableCell colSpan={3} align="right">
                <Box sx={{ display: 'grid', gridTemplateColumns: '130px 120px 62px', justifyContent: 'end', columnGap: '3px', textAlign: 'right' }}>
                  <span>Mês anterior</span>
                  <span>{monthKey}</span>
                  <span>%</span>
                </Box>
              </StyledTableCell>
            </TableRow>
          </StyledTableHead>
          <TableBody>
            {debitGroups.length === 0 && (
              <TableRow>
                <StyledTableCell colSpan={4}>
                  <EmptyState>Nenhum débito no período</EmptyState>
                </StyledTableCell>
              </TableRow>
            )}
            {debitGroups.map((groupData, index) => (
              <GroupComponent
                key={groupData.group.id || index}
                groupData={groupData}
                monthKey={monthKey}
                previousMonthKey={previousMonthKey}
                transactionsByCategory={transactionsByCategory}
                navigate={navigate}
                isOpen={!!openGroups[groupData.group.id]}
                openCategories={openCategories}
                onToggleGroup={toggleGroup}
                onToggleCategory={toggleCategory}
              />
            ))}
          </TableBody>
          <TableFooter>
            <StyledTableRow hover>
              <StyledTableCell sx={{ fontWeight: 700, fontSize: '1rem' }}>
                Total Débitos
              </StyledTableCell>
              <MoneyCell align="right" colSpan={3} sx={{ fontWeight: 700, fontSize: '1rem' }}>
                <TotalWithComparison
                  currentValue={result.debitBalance[monthKey]}
                  previousValue={result.debitBalance[previousMonthKey]}
                  isDebit={true}
                />
              </MoneyCell>
            </StyledTableRow>
          </TableFooter>
        </StyledTable>
      </StyledPaper>
        </>
      )}
    </>
  );
}

export default Report;
